import React, { useContext, useState } from "react";
import { useMutation } from "@apollo/client";
// Components
import { Button, FieldDate, Modal } from "components";
// Helpers
import { CommonContext } from "config/commonProvider";
import { getBUFromLocalStorage } from "helpers/storage";
// API
import { UPDATE_REGULATORY_TRANSACTIONS_DATE } from "api/Regulatory/mutations";
// Types
import { TransactionType } from "components/Regulatory/Regulatory";
import {
  RegulatoryTransactionApprovalsChangeDate,
  RegulatoryTransactionApprovalsChangeDateVariables,
} from "api/Regulatory/types/RegulatoryTransactionApprovalsChangeDate";
// Hooks
import { useEventRequest } from "hooks";

interface ChangeDateProps {
  selectedTransactions: string[];
  transactionType: TransactionType;
  onSuccessSubmit: () => void;
}

export const ChangeDate: React.FC<ChangeDateProps> = ({
  selectedTransactions = [],
  onSuccessSubmit,
  transactionType,
}) => {
  const [modalVisible, changeModalVisibility] = useState<boolean>(false);
  const [date, changeDate] = useState<Date>(new Date());
  const { showNotification } = useContext(CommonContext);
  const { eventsRequest } = useEventRequest();
  const businessUnit = getBUFromLocalStorage();

  const [updateTransactionsDate, { loading }] = useMutation<
    RegulatoryTransactionApprovalsChangeDate,
    RegulatoryTransactionApprovalsChangeDateVariables
  >(UPDATE_REGULATORY_TRANSACTIONS_DATE, {
    variables: {
      input: {
        businessUnit,
        transactionDate: date || new Date(),
        transactionType,
        regulatoryTransactionApprovals: selectedTransactions,
      },
    },
  });

  const { length: transactionCount } = selectedTransactions;
  const thisOrThese: string = transactionCount === 1 ? "this" : "these";
  const sOrNot: string = transactionCount !== 1 ? "s" : "";
  const totalOrNot: string = transactionCount !== 1 ? `${transactionCount} ` : "";
  const dateToday = new Date();

  const handleCloseModal = (): void => changeModalVisibility(false);

  const handleConfirmClick = async (): Promise<void> => {
    const { data } = await updateTransactionsDate();
    const errors = data?.regulatoryTransactionApprovalsChangeDate?.errors;

    eventsRequest({
      name: "regulatory-transaction-date-changed",
      details: { businessUnitId: businessUnit, selectedTransactions, newDate: date },
    });

    if (errors) {
      showNotification({
        variant: "error",
        message: `Error updating movement date.`,
      });
    } else {
      showNotification({
        message: `Movement date successfully updated.`,
      });
      onSuccessSubmit();
      handleCloseModal();
    }
  };

  return (
    <>
      <Button
        caption="Change date"
        colour="grey"
        disabled={!transactionCount}
        onClick={(): void => changeModalVisibility(true)}
        variant="hollow"
      />
      <Modal
        showCloseButton
        active={modalVisible}
        handleClose={handleCloseModal}
        title={`Change movement date of ${thisOrThese} pending movement${sOrNot}?`}
        subTitle={`Confirm you want to change the movement date of ${thisOrThese} ${totalOrNot}pending movement${sOrNot}.`}
        actions={{
          secondary: {
            onClick: handleCloseModal,
            caption: "Cancel",
          },
          primary: {
            onClick: handleConfirmClick,
            caption: "Confirm",
            disabled: loading,
            requesting: loading,
          },
        }}
      >
        <FieldDate
          label="New movement date"
          inputProps={{
            name: "transactionDate",
            value: date,
            maxDate: dateToday,
            onChange: (newDate: Date): void => changeDate(newDate),
          }}
        />
      </Modal>
    </>
  );
};
